import * as React from "react";
import { TRIAL_CONTACT_COUNT, TRIAL_CONTACTS_PHRASE } from "@/lib/trial";
import { Link, useLocation, useNavigate } from "@tanstack/react-router";
import { useAuth } from "@/hooks/use-auth";
import { HOME_FAQ_ITEMS } from "./home-faq";
import { AgentDartWalkthrough } from "@/components/marketing/agentdart-walkthrough";
import "@/styles/agentdart-walkthrough.css";
import {
  COMPANY_TYPE_ROWS,
  TOTAL_LIST_COUNT,
  VERTICAL_ROWS,
  type IndexRow,
} from "./rankings-index";
import {
  handleHomeClick,
  useHeaderCompaction,
  useMobileTrialVisibility,
} from "./home-behavior";
import { Top10FinderCard, type FinderCardId } from "./top10-finder";


/**
 * CapitalDart homepage.
 *
 * Markup, copy, section order and class names follow the approved homepage
 * reference exactly. Styling lives in src/styles/home.css, scoped under
 * .cd-home. Interaction behavior (demo loading, sticky bar visibility,
 * header compaction, analytics) is wired in a later pass.
 */

const SIGNUP = { to: "/auth", search: { mode: "signup" as const } } as const;
const SIGNIN = { to: "/auth", search: { mode: "signin" as const } } as const;

export function SiteHeader() {
  const { session, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isAuthed = Boolean(session);
  const headerRef = React.useRef<HTMLElement | null>(null);
  const credentialsRef = React.useRef<HTMLParagraphElement | null>(null);
  useHeaderCompaction(headerRef, credentialsRef);

  return (
    <header className="site-header" ref={headerRef}>
      <div className="shell header-inner">

        <div className="header-brand">
          <a className="brand" href="#" aria-label="CapitalDart home">
            <span className="brand-mark" aria-hidden="true">
              ➤
            </span>
            <span>
              Capital<span className="brand-gold">Dart</span>
            </span>
            <sup>™</sup>
          </a>
          <p className="brand-credentials" ref={credentialsRef}>
            <span>BUILT BY INVESTMENT BANKERS</span>
          </p>

        </div>
        <nav className="desktop-nav" aria-label="Main navigation">
          <a
            href="https://demo.capitaldart.com"
            target="_blank"
            rel="noopener"
            data-demo-placement="header-nav"
          >
            Interactive Demo
          </a>
          <a href={isHome ? "#pricing" : "/#pricing"}>Pricing</a>
          <a href={isHome ? "#faq" : "/#faq"}>FAQ</a>
        </nav>
        <div className="header-actions">
          {loading ? null : isAuthed ? (
            <>
              <button
                type="button"
                className="login"
                onClick={async () => {
                  await signOut();
                  navigate({ to: "/" });
                }}
              >
                Sign out
              </button>
              <Link className="button button-gold button-small" to="/dashboard">
                Dashboard <span aria-hidden="true">→</span>
              </Link>
            </>
          ) : (
            <>
              <Link className="login" to={SIGNIN.to} search={SIGNIN.search}>
                Log in
              </Link>
              <Link
                className="button button-gold button-small"
                to={SIGNUP.to}
                search={SIGNUP.search}
                data-cta="header"
              >
                Start free trial <span aria-hidden="true">→</span>
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero() {
  return (
    <section className="hero" aria-labelledby="hero-title">
      <div className="shell">
        <h1 id="hero-title">
          Everything You Need
          <br />
          to <em>Raise Capital.</em>
        </h1>
        <p className="hero-promise">
          <strong>70,000+ vetted investors.</strong> One complete AI platform.{" "}
          <span className="platform-promise">Strategy to closing.</span>
        </p>
        <p className="hero-audience">
          For companies, real estate sponsors, fund managers and borrowers.
        </p>
        <div className="hero-conversion">
          <Link
            className="button button-gold button-hero"
            to={SIGNUP.to}
            search={SIGNUP.search}
            data-cta="hero"
          >
            <span className="hero-button-copy">
              <small>One price. No subscription. No credit card required.</small>
              <strong>Start my free trial</strong>
            </span>
            <span aria-hidden="true">→</span>
          </Link>
        </div>
        <div className="capability-strip" aria-label="Explore the platform">
          <a href="#find">
            <span className="cap-label">
              FIND <span aria-hidden="true">↗</span>
            </span>
            <h2>The right capital.</h2>
            <p>
              Investors · Acquirers · LPs{" "}
              <br />
              Seed/GP Stakes · Lenders
            </p>
          </a>
          <a href="#diligence">
            <span className="cap-label">
              DILIGENCE <span aria-hidden="true">↗</span>
            </span>
            <h2>Investor-ready materials.</h2>
            <p>
              Deck · Financial Model · Comps{" "}
              <br />
              Valuation · Term Sheet
            </p>
          </a>
          <a href="#connect">
            <span className="cap-label">
              CONNECT <span aria-hidden="true">↗</span>
            </span>
            <h2>A relevant approach.</h2>
            <p>
              Bespoke Outreach · Warm Intros{" "}
              <br />
              Investor Signals · Conferences
            </p>
          </a>
          <a href="#execute">
            <span className="cap-label">
              EXECUTE <span aria-hidden="true">↗</span>
            </span>
            <h2>A coordinated raise.</h2>
            <p>Strategy · Deal Tracking · Team access</p>
            <div className="platform-access-links" aria-label="Ways to use AgentDart">
              <span className="platform-access-pill">
                <MarkIcon />
                CapitalDart
              </span>
              <span className="platform-access-pill">
                <ClaudeIcon />
                Claude
              </span>
              <span className="platform-access-pill">
                <OpenAiIcon />
                ChatGPT
              </span>
              <span className="platform-access-pill agentdart-access-pill">
                <MarkIcon />
                <span>AgentDart</span>
                <span className="agentdart-access-subtitle">Investment Banker Agent</span>
              </span>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}

function MarkIcon() {
  return (
    <img
      src="/capitaldart-mark.png"
      alt=""
      width="14"
      height="14"
      aria-hidden="true"
      style={{ display: "inline-block", flexShrink: 0, objectFit: "contain" }}
    />
  );
}

function ClaudeIcon() {
  return (
    <svg
      role="img"
      aria-label="Claude"
      viewBox="0 0 24 24"
      width="14"
      height="14"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "inline-block", flexShrink: 0 }}
    >
      <path
        d="m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z"
        fill="#D97757"
      />
    </svg>
  );
}

function OpenAiIcon() {
  return (
    <svg
      role="img"
      aria-label="OpenAI"
      viewBox="0 0 24 24"
      width="14"
      height="14"
      xmlns="http://www.w3.org/2000/svg"
      style={{ display: "inline-block", flexShrink: 0 }}
    >
      <path
        d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.872zm16.5963 3.8558L13.1038 8.364 15.1192 7.2a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.407-.667zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.0379-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"
        fill="#10A37F"
      />
    </svg>
  );
}

function AgentDartSection() {
  return (
    <section id="agentdart" className="section agentdart-section" aria-labelledby="agentdart-title">
      <div className="shell">
        <div className="work-style-heading">
          <p className="eyebrow">YOUR RAISE. YOUR WAY.</p>
            <h2 id="agentdart-title">
              One platform.{" "}
              <br />
              <em>You choose how to run it.</em>
            </h2>
        </div>
        <div className="agentdart-intro work-style-options">
          <article className="work-style-option">
            <p className="eyebrow">CHAT. YOUR PERSONAL INVESTMENT BANKING ANALYST.</p>
            <h3>
              Ask for it.{" "}
              <br />
              It gets done.
            </h3>
            <p>
              Simple to use directly, or tell the chat what you need and it does it inside your
              deal. You review every result and decide what happens next.
            </p>
            <div className="platform-access" aria-label="Ways to use CapitalDart">
              <p>WORKS IN</p>
              <div className="platform-access-links">
                <span className="platform-access-pill">
                  <MarkIcon />
                  CapitalDart
                </span>
                <Link className="platform-access-pill" to="/connect/claude">
                  <ClaudeIcon />
                  Claude
                </Link>
                <Link className="platform-access-pill" to="/connect/chatgpt">
                  <OpenAiIcon />
                  ChatGPT
                </Link>
              </div>
            </div>
            <div className="work-style-demo-cta">
              <p>See the actual product before you sign up.</p>
              <a
                className="play-button"
                href="https://demo.capitaldart.com"
                target="_blank"
                rel="noopener"
                aria-label="Open the interactive demo in a new tab"
                data-demo-placement="demo-section"
              >
                <span className="play-icon" aria-hidden="true">
                  ▶
                </span>{" "}
                Open the interactive demo <span aria-hidden="true">↗</span>
              </a>
            </div>
          </article>
          <article className="work-style-option">
            <p className="eyebrow">AGENTDART. YOUR PERSONAL INVESTMENT BANKER AGENT.</p>
            <h3>
              Give AgentDart a goal.{" "}
              <br />
              Get results.
            </h3>
            <p>
              Set permissions and limits. You are always in control. Add AgentDart (optional) whenever
              you want more done for you.
            </p>
            <div className="agentdart-pill-row">
              <span className="platform-access-pill agentdart-access-pill">
                <MarkIcon />
                <span>AgentDart</span>
                <span className="agentdart-access-subtitle">Investment Banker Agent</span>
              </span>
            </div>
            <AgentDartWalkthrough variant="home" />
            <Link className="play-button" to="/agentdart">
              See how AgentDart works <span aria-hidden="true">↗</span>
            </Link>
          </article>
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  return (
    <section id="how-it-works" className="paper section">
      <div className="shell">
        <div className="section-top">
          <div>
            <p className="eyebrow">START WITH YOUR OWN DEAL</p>
            <h2>
              See the value.{" "}
              <br />
              <em>Before you pay.</em>
            </h2>
          </div>
          <p className="section-intro">
            Create an account and upload your pitch materials. Your free trial gives you a concrete
            starting point for the raise.
          </p>
        </div>
        <div className="trial-deliverables">
          <article>
            <span className="step">01 / YOUR INVESTORS</span>
            <h3>Get matched investor contacts.</h3>
            <p>
              Decision makers at investor firms, ranked by likelihood of investing, with the rationale
              behind each match.
            </p>
          </article>
          <article>
            <span className="step">02 / YOUR OUTREACH</span>
            <h3>Investor messages that get replies.</h3>
            <p>
              Your value drivers, combined with their past investments and signal-driven personalization.
            </p>
          </article>
          <article>
            <span className="step">03 / YOUR MATERIALS</span>
            <h3>An Investor Readiness Report.</h3>
            <p>
              Your readiness score, slide-by-slide deck comments, pitch strategy, and investor Q&A
              prep.
            </p>
            <Link className="inline-link" to="/sample-report" data-evidence="sample-report">
              Read the published sample <span aria-hidden="true">↗</span>
            </Link>
          </article>
        </div>
        <div className="trial-bottom">
          <Link
            className="button button-navy"
            to={SIGNUP.to}
            search={SIGNUP.search}
            data-cta="trial-details"
          >
            Try it with my deal <span aria-hidden="true">→</span>
          </Link>
        </div>
      </div>
    </section>
  );
}

function PlatformSection() {
  return (
    <section id="platform" className="paper section platform-section">
      <div className="shell">
        <div className="section-top">
          <div>
            <p className="eyebrow">THE FULL CAPITAL RAISING WORKFLOW</p>
            <h2>
              Four steps.{" "}
              <br />
              <em>Every part of the raise.</em>
            </h2>
          </div>
          <p className="section-intro">
            CapitalDart combines investor matching, diligence, outreach and deal management in one
            AI capital raising platform.
          </p>
        </div>
        <div id="find" className="platform-row">
          <div className="row-title">
            <span className="row-index">01 / FIND</span>
            <h3>
              Find investors who{" "}
              <br />
              fit your company.
            </h3>
          </div>
          <div className="row-body">
            <p>
              Match your industry, stage, geography and capital needs to investor criteria and
              recent investments. Identify the partner, principal or deal contact to approach, not
              just the firm.
            </p>
            <div className="tag-list">
              <span>Contact-level investment criteria</span>
              <span>Verified email &amp; LinkedIn</span>
              <span>Prior investments</span>
              <span>Fit score &amp; rationale</span>
            </div>
          </div>
        </div>
        <div id="diligence" className="platform-row">
          <div className="row-title">
            <span className="row-index">02 / DILIGENCE</span>
            <h3>
              Prepare for the{" "}
              <br />
              questions ahead.
            </h3>
          </div>
          <div className="row-body">
            <p>
              Review your deck and financial model, assess your positioning against market comps,
              and develop a raise strategy and indicative terms.
            </p>
            <div className="report-grid">
              <details open>
                <summary>Investor Readiness Report</summary>
                <p>
                  Deck review, investor objections and actionable recommendations. Free on every
                  deal.
                </p>
              </details>
              <details>
                <summary>Financial Model Review</summary>
                <p>
                  A first-pass review of your Excel model with findings tied to worksheets and
                  cells, assumptions to challenge and diligence questions.
                </p>
              </details>
              <details>
                <summary>Raise Strategy Report</summary>
                <p>
                  Pacing, channels, positioning by investor type, send order and a weekly working
                  plan.
                </p>
              </details>
              <details>
                <summary>Market Comps &amp; Valuation</summary>
                <p>
                  Transaction benchmarks, comparable deals and an implied valuation range for
                  companies raising equity or selling. Informational, not a valuation opinion.
                </p>
              </details>
              <details>
                <summary>Indicative Term Sheet</summary>
                <p>
                  Draft economics, structure and key terms adapted to a company, fund, real estate
                  or debt transaction.
                </p>
              </details>
            </div>
          </div>
        </div>
        <div id="connect" className="platform-row">
          <div className="row-title">
            <span className="row-index">03 / CONNECT</span>
            <h3>
              Make your outreach{" "}
              <br />
              worth reading.
            </h3>
          </div>
          <div className="row-body">
            <p>
              Prepare email and LinkedIn drafts in your voice, informed by each investor’s activity.
              Explore potential warm introductions and follow up with relevant context.
            </p>
            <div className="tag-list">
              <span>Bespoke outreach</span>
              <span>Warm introduction paths</span>
              <span>Investor signals &amp; investments</span>
              <span>LinkedIn posts</span>
              <span>Conferences</span>
            </div>
          </div>
        </div>
        <div id="execute" className="platform-row">
          <div className="row-title">
            <span className="row-index">04 / EXECUTE</span>
            <h3>
              Keep your entire{" "}
              <br />
              raise moving.
            </h3>
          </div>
          <div className="row-body">
            <p>
              Every contact sits in a shared pipeline with a status, the last touch and what comes
              next. Follow-ups queue up when they are due, stale conversations get flagged, and Deal
              Update Reports summarize where the raise stands so you and your team always know the
              next move.
            </p>
            <div className="tag-list">
              <span>Shared pipeline</span>
              <span>Status funnel</span>
              <span>Follow-up queue</span>
              <span>Stale-contact alerts</span>
              <span>Deal Update Reports</span>
              <span>Team access</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function AudienceSection() {
  // Only one card's finder is open at a time; switching cards unmounts the
  // other panel, which clears its selections and results.
  const [openCard, setOpenCard] = React.useState<FinderCardId | null>(null);
  const toggleCard = React.useCallback(
    (id: FinderCardId) => setOpenCard((current) => (current === id ? null : id)),
    [],
  );
  return (
    <section className="section audience-section">
      <div className="shell">
        <div className="section-top">
          <div>
            <p className="eyebrow">MATCHED TO YOUR MANDATE</p>
            <h2>
              Different raises.{" "}
              <br />
              <em>Different investor universes.</em>
            </h2>
          </div>
          <p className="section-intro">
            The right capital depends on what you’re raising, how it is structured and who actually
            invests in transactions like yours.
          </p>
        </div>
        <div className="audience-grid">
          <article>
            <span className="audience-icon" aria-hidden="true">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2.5" y="7" width="19" height="13" rx="2" />
                <path d="M9 7V5.5A1.5 1.5 0 0 1 10.5 4h3A1.5 1.5 0 0 1 15 5.5V7" />
                <path d="M2.5 12h19" />
              </svg>
            </span>
            <h3>
              Companies raising equity{" "}
              <br />
              or selling a business.
            </h3>
            <p>
              Find venture capital, growth equity, private equity, family offices, asset managers
              and strategic acquirers. Match by stage, sector, check size and transaction activity.
            </p>
            <p className="audience-proof">
              50,100+ VCs, private equity firms, family offices and strategic acquirers.
            </p>
            <div className="tag-list">
              <span>Stage</span>
              <span>Sector</span>
              <span>Check size</span>
              <span>Recent deals</span>
              <span>Geography</span>
            </div>
            <Top10FinderCard
              cardId="company"
              buttonLabel="Find Top 10 Investors for Your Company"
              open={openCard === "company"}
              onToggle={toggleCard}
            />
          </article>
          <article>
            <span className="audience-icon" aria-hidden="true">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 20V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v15" />
                <path d="M15 20V10h4a1 1 0 0 1 1 1v9" />
                <path d="M2.5 20h19" />
                <path d="M7 8h2M11 8h1M7 12h2M11 12h1M7 16h2M11 16h1" />
              </svg>
            </span>
            <h3>
              Real estate sponsors{" "}
              <br />
              raising for deals or funds.
            </h3>
            <p>
              Identify real estate investors, institutional LPs, family offices and lenders by asset
              class, equity or debt preference, and investment size.
            </p>
            <p className="audience-proof">13,400+ real estate investors and lenders.</p>
            <div className="tag-list">
              <span>Property type</span>
              <span>Equity or debt</span>
              <span>Deal size</span>
              <span>Geography</span>
            </div>
            <Top10FinderCard
              cardId="real_estate"
              buttonLabel="Find Top 10 Investors or Lenders for Your Property"
              open={openCard === "real_estate"}
              onToggle={toggleCard}
            />
          </article>
          <article>
            <span className="audience-icon" aria-hidden="true">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 9.5 12 4l9 5.5" />
                <path d="M5.5 9.5V18M10 9.5V18M14 9.5V18M18.5 9.5V18" />
                <path d="M3 20.5h18" />
              </svg>
            </span>
            <h3>
              Fund managers{" "}
              <br />
              raising from LPs.
            </h3>
            <p>
              Find pensions, endowments, foundations, funds of funds, insurers, family offices and
              other institutional LPs, plus seed investors and firms that back emerging managers,
              with a program fit for your strategy.
            </p>
            <p className="audience-proof">
              2,600+ institutional LPs, plus seed and emerging-manager investors.
            </p>
            <div className="tag-list">
              <span>Strategy</span>
              <span>Program fit</span>
              <span>Ticket size</span>
              <span>Allocation stage</span>
              <span>Geography</span>
            </div>
            <Top10FinderCard
              cardId="fund"
              buttonLabel="Find Top 10 Investors for Your Fund"
              open={openCard === "fund"}
              onToggle={toggleCard}
            />
          </article>
          <article>
            <span className="audience-icon" aria-hidden="true">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M9 15 15 9" />
                <circle cx="9.5" cy="9.5" r="1.1" />
                <circle cx="14.5" cy="14.5" r="1.1" />
              </svg>
            </span>
            <h3>
              Borrowers seeking{" "}
              <br />
              debt financing.
            </h3>
            <p>
              Identify private credit funds, direct lenders, banks and specialty finance providers
              by sector, size and financing structure.
            </p>
            <p className="audience-proof">
              8,000+ debt providers: banks, specialty lenders and private credit funds.
            </p>
            <div className="tag-list">
              <span>Structure</span>
              <span>Sector</span>
              <span>Loan size</span>
              <span>Lender type</span>
              <span>Geography</span>
            </div>
            <Top10FinderCard
              cardId="business_loan"
              buttonLabel="Find Top 10 Lenders for Your Business"
              open={openCard === "business_loan"}
              onToggle={toggleCard}
            />
          </article>
        </div>
        <p className="data-note audience-note">
          Directory figures as displayed September 12, 2026.
        </p>
      </div>
    </section>
  );
}

function NetworkSection() {
  return (
    <section className="network-section section">
      <div className="shell">
        <div className="network-top">
          <div>
            <p className="eyebrow">CAPITALDART'S INVESTOR NETWORK</p>
            <div className="network-number">
              70,000<span>+</span>
            </div>
            <h2>
              Vetted investors.{" "}
              <br />
              Contact-level intelligence.
            </h2>
          </div>
          <div className="network-description">
            <p>
              A directory curated by investment bankers, covering institutional investors,
              acquirers, LPs and lenders. Matching considers both the firm’s mandate and the
              relevant person at the firm.
            </p>
          </div>
        </div>
        <div className="network-categories" aria-label="Selected investor categories">
          <div>
            <strong>19,777</strong>
            <span>Venture capital</span>
          </div>
          <div>
            <strong>13,600</strong>
            <span>Private equity</span>
          </div>
          <div>
            <strong>10,430</strong>
            <span>Strategic investors</span>
          </div>
          <div>
            <strong>8,711</strong>
            <span>Real estate investors (equity)</span>
          </div>
          <div>
            <strong>4,179</strong>
            <span>Family offices</span>
          </div>
          <div>
            <strong>3,652</strong>
            <span>Banks &amp; specialty lenders</span>
          </div>
          <div>
            <strong>2,600+</strong>
            <span>Institutional LPs</span>
          </div>
          <div>
            <strong>2,320</strong>
            <span>Asset managers</span>
          </div>
        </div>
        <p className="data-note">
          Selected categories from the CapitalDart directory. Institutional LPs covers pensions,
          endowments, foundations, funds of funds, insurers and sovereign funds.
        </p>
      </div>
    </section>
  );
}

/**
 * Index of every ranking list page, grouped by vertical and company type.
 * Both panels and every row are rendered on the server; the tabs only show
 * and hide what is already in the DOM.
 */
function RankingsIndex() {
  const [tab, setTab] = React.useState<"vertical" | "company">("vertical");

  return (
    <section className="rankings-section section" aria-labelledby="rankings-title">
      <div className="shell research">
        <div className="research-intro">
          <span className="eyebrow">TOP INVESTORS BY SECTOR AND STAGE</span>
          <h3 id="rankings-title">
            Who is investing{" "}
            <br />
            in your sector?
          </h3>
          <p>
            Investor rankings for 84 sectors and four stages, drawn from 2025/2026 deal activity
            matched to the CapitalDart directory.
          </p>
        </div>
        <div className="research-tabs" role="tablist" aria-label="Investor rankings index">
          <button
            type="button"
            role="tab"
            id="rankings-tab-vertical"
            aria-selected={tab === "vertical"}
            aria-controls="rankings-panel-vertical"
            className={tab === "vertical" ? "is-active" : undefined}
            onClick={() => setTab("vertical")}
          >
            By vertical
          </button>
          <button
            type="button"
            role="tab"
            id="rankings-tab-company"
            aria-selected={tab === "company"}
            aria-controls="rankings-panel-company"
            className={tab === "company" ? "is-active" : undefined}
            onClick={() => setTab("company")}
          >
            By company type
          </button>
        </div>

        <div className="research-actions">
          <Link className="inline-link" to="/lists">
            Browse all {TOTAL_LIST_COUNT} investor rankings <span aria-hidden="true">↗</span>
          </Link>
        </div>

        <div className="research-scroll">
          <div
            role="tabpanel"
            id="rankings-panel-vertical"
            aria-labelledby="rankings-tab-vertical"
            className={`research-panel${tab === "vertical" ? "" : " is-hidden"}`}
          >
            <ul className="research-columns">
              {VERTICAL_ROWS.map((row) => (
                <RankingsIndexRow key={row.category} row={row} />
              ))}
            </ul>
          </div>

          <div
            role="tabpanel"
            id="rankings-panel-company"
            aria-labelledby="rankings-tab-company"
            className={`research-panel${tab === "company" ? "" : " is-hidden"}`}
          >
            <ul className="research-columns">
              {COMPANY_TYPE_ROWS.map((row) => (
                <RankingsIndexRow key={row.category} row={row} />
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

function RankingsIndexRow({ row }: { row: IndexRow }) {
  return (
    <li className="research-row">
      <span className="research-row-name">{row.name}</span>
      <span className="research-row-stages">
        {row.chips.map((chip) => (
          <a
            key={chip.slug}
            href={`/lists/${chip.slug}`}
            title={chip.fullName}
            aria-label={chip.fullName}
          >
            {chip.label}
          </a>
        ))}
      </span>
    </li>
  );
}

function PricingSection() {
  return (
    <section id="pricing" className="section">
      <div className="shell">
        <div className="section-top">
          <div>
            <p className="eyebrow">TRY IT FIRST. EXPAND WHEN YOU’RE READY.</p>
            <h2>
              One payment.{" "}
              <br />
              <em>No subscription.</em>
            </h2>
          </div>
          <p className="section-intro">
            Evaluate the contacts, drafts and readiness report on your own deal before choosing the
            paid plan.
          </p>
        </div>
        <div className="pricing-grid">
          <article className="free-plan">
            <span className="plan-label">START HERE</span>
            <h3>Your free trial</h3>
            <p className="price">$0</p>
            <p className="price-caption">No credit card required.</p>
            <ul className="check-list">
              <li>{TRIAL_CONTACTS_PHRASE}</li>
              <li>Personalized outreach drafts</li>
              <li>An Investor Readiness Report</li>
            </ul>
            <Link
              className="button button-navy"
              to={SIGNUP.to}
              search={SIGNUP.search}
              data-cta="pricing-free"
            >
              Start my free trial <span aria-hidden="true">→</span>
            </Link>
          </article>
          <article className="paid-plan">
            <div className="paid-heading">
              <span className="plan-label">WHEN YOU’RE READY FOR MORE</span>
              <h3>The full plan</h3>
              <div className="paid-price">
                <p className="price">$250</p>
                <p className="price-qualifier">
                  after rebate for your feedback, or{" "}
                  <br />
                  {" "}$0 if we use your comments.
                </p>
              </div>
            </div>
            <div className="paid-columns">
              <ul className="check-list">
                <li>500 investor contacts: 1 credit per contact</li>
                <li>Outreach drafts and pipeline tracking</li>
                <li>Chat and AgentDart included</li>
                <li>5 analysis reports, plus unlimited Deal Update Reports</li>
              </ul>
              <div className="report-allowance">
                <strong>5 analysis reports, plus unlimited Deal Update Reports</strong>
                <ul className="report-list">
                  <li>Investor Readiness Report</li>
                  <li>Financial Model Review</li>
                  <li>Raise Strategy</li>
                  <li>Market Comps and Valuation</li>
                  <li>Indicative Term Sheet</li>
                </ul>
                <p>
                  Top-ups: 100 credits for $100.{" "}
                  <br />
                  Additional reports: 25 credits.{" "}
                  <br />
                  Additional team seat: 10 credits on acceptance.
                </p>
              </div>
            </div>
            <div className="rebate">
              <strong>Two rebates, up to $500 back</strong>
              <p>
                Pay $500. Complete the experience questionnaire after your first delivery and you
                get $250 back. We value your feedback in making CapitalDart better. If we use your
                comments on the site or in our materials, you get another $250 back.
              </p>
            </div>
          </article>
        </div>
        <p className="pricing-compare">
          <Link className="inline-link" to="/compare">
            Compare CapitalDart with other approaches <span aria-hidden="true">↗</span>
          </Link>
        </p>
      </div>
    </section>
  );
}

function FaqSection() {
  return (
    <section id="faq" className="section faq-section">
      <div className="shell faq-grid">
        <div className="faq-heading">
          <p className="eyebrow">QUESTIONS BEFORE YOU START</p>
          <h2>
            Clear answers.{" "}
            <br />
            <em>Then try it.</em>
          </h2>
          <Link className="inline-link" to="/faq">
            Explore all FAQs <span aria-hidden="true">↗</span>
          </Link>
        </div>
        <div className="faq-list">
          {HOME_FAQ_ITEMS.map((item, index) => (
            <details key={item.question} open={index === 0}>
              <summary>{item.question}</summary>
              <p>
                {item.answer}
                {item.link ? (
                  <>
                    {" "}
                    <Link to={item.link.to}>{item.link.label}</Link>
                  </>
                ) : null}
              </p>
            </details>
          ))}
        </div>

      </div>
    </section>
  );
}

function ClosingSection() {
  return (
    <section className="closing">
      <div className="shell">
        <p className="eyebrow">EVERYTHING YOU NEED TO RAISE CAPITAL.</p>
        <h2>
          Put CapitalDart to work{" "}
          <br />
          <em>on your deal.</em>
        </h2>
        <p>Investor contacts. Strategy. Analysis. Outreach. One AI platform.</p>
        <p className="closing-compare">
          <Link to="/compare">Compare us: the complete platform at a lower price.</Link>
        </p>
        <Link
          className="button button-gold button-hero"
          to={SIGNUP.to}
          search={SIGNUP.search}
          data-cta="closing"
        >
          Start my free trial <span aria-hidden="true">→</span>
        </Link>
        <p className="closing-note">No subscription. No credit card required.</p>
      </div>
    </section>
  );
}

export function SiteFooter() {
  return (
    <footer>
      <div className="shell">
        <div className="footer-top">
          <a className="brand" href="#">
            Capital<span className="brand-gold">Dart</span>
            <sup>™</sup>
          </a>
          <p>AI investor matching, diligence, outreach and deal tracking.</p>
          <a href="mailto:support@capitaldart.com">support@capitaldart.com</a>
        </div>
        <div className="footer-links">
          <span>© 2026 CapitalDart</span>
          <Link to="/compare">Compare</Link>
          <Link to="/lists">Investor rankings</Link>
          <Link to="/agentdart">AgentDart</Link>
          <Link to="/connect">Claude / ChatGPT</Link>
          <Link to="/faq">FAQ</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
          <Link to="/about">About</Link>
          <Link to="/help">Help</Link>
          <Link to="/partners">Partners</Link>
          <Link to="/contact">Contact</Link>
        </div>
      </div>
    </footer>
  );
}

function MobileTrialBar() {
  const visible = useMobileTrialVisibility();
  return (
    <div className="mobile-trial" id="mobile-trial" hidden={!visible}>

      <div>
        <strong>Your first {TRIAL_CONTACT_COUNT} contacts, free.</strong>
        <span>No credit card required.</span>
      </div>
      <Link
        className="button button-gold button-small"
        to={SIGNUP.to}
        search={SIGNUP.search}
        data-cta="mobile-sticky"
      >
        Start free trial <span aria-hidden="true">→</span>
      </Link>
    </div>
  );
}

export function HomePage() {
  // Smooth scrolling and anchor offset belong to the homepage only, so the
  // class is added to <html> while this page is mounted and removed on exit.
  React.useEffect(() => {
    document.documentElement.classList.add("cd-home-scroll");
    return () => document.documentElement.classList.remove("cd-home-scroll");
  }, []);

  return (
    <div className="cd-home" onClick={handleHomeClick}>
      <a className="skip" href="#main">
        Skip to content
      </a>
      <SiteHeader />
      <main id="main">
        <Hero />
        <NetworkSection />
        <AgentDartSection />
        <AudienceSection />
        <PlatformSection />
        <PricingSection />
        <HowItWorksSection />
        <FaqSection />
        <RankingsIndex />
        <ClosingSection />
      </main>
      <SiteFooter />
      <MobileTrialBar />
    </div>
  );
}
